import googleTypesToIconType from "@utils/google_types_to_icon_type";
import { getConfig } from "@lib/use_config";
import { getContent } from "@lib/use_content";
import Session from "@lib/session";
import {
  LocationsForAutocompleteDocument,
  LocationsForAutocompleteQuery,
  LocationsForAutocompleteQueryVariables,
} from "@graphql/api";

export async function appendMoreOptionsLink(results: be.LocationPrediction[]) {
  // getContent runs apolloClient.query under Apollo's default errorPolicy "none",
  // which REJECTS on any GraphQL/network error. The "more options" affordance is
  // optional and its label is a content token (no safe hardcoded fallback), so on
  // a content-read failure skip it rather than propagate the reject and abort the
  // whole autocomplete.
  let title = "";
  try {
    const { data } = await getContent(["beAutocompleteMoreOptionsButton"]);
    title = data?.content?.beAutocompleteMoreOptionsButton ?? "";
  } catch (e) {
    console.error("Failed to load autocomplete more-options content:", e);
    return;
  }

  return results.push({
    key: "META",
    source: "searchMeta",
    title,
    type: "generic",
    options: { forceGoogle: true },
  });
}

export async function googleSearch(
  searchTerm: string,
  searchCenter?: be.LatLng,
  searchRadius?: number,
  limitLocations?: number,
): Promise<be.LocationPrediction[]> {
  const { data } = await getConfig(["locationBiasRadius", "locationBiasStrictBounds"]);
  const locationBiasRadius = data?.config?.locationBiasRadius,
    locationBiasStrictBounds = data?.config?.locationBiasStrictBounds;

  const acService = new google.maps.places.AutocompleteService();
  const acOptions = {
    types: ["establishment", "geocode"],
    input: searchTerm,
  } as google.maps.places.AutocompletionRequest;

  if (searchCenter?.lat) {
    const radius = locationBiasRadius ? locationBiasRadius : searchRadius;
    if (locationBiasStrictBounds) {
      acOptions.locationRestriction = new google.maps.Circle({
        center: searchCenter,
        radius,
      }).getBounds();
    } else {
      acOptions.locationBias = new google.maps.Circle({
        center: new google.maps.LatLng(searchCenter.lat, searchCenter.lng),
        radius,
      });
    }
  }

  const response = await acService.getPlacePredictions(acOptions);
  let locationResults: google.maps.places.AutocompletePrediction[] = response?.predictions || [];
  if (limitLocations) locationResults = locationResults.slice(0, limitLocations);

  return locationResults.map((place, i, array) => ({
    final: (array.length - 1 == i).toString(),
    source: "google",
    title: place.structured_formatting.main_text,
    description: place.structured_formatting.secondary_text,
    key: place.place_id,
    type: googleTypesToIconType(place.types),
  }));
}

async function hudsonSearch(searchTerm: string, serviceArea?: number, limitLocations?: number) {
  const client = global.window.apolloClient;
  const queryVars: LocationsForAutocompleteQueryVariables = {
    search: searchTerm,
    serviceAreaId: serviceArea || Session.getConfig().serviceArea,
  };
  const { data }: { data: LocationsForAutocompleteQuery } = await client.query({
    query: LocationsForAutocompleteDocument,
    variables: queryVars,
  });

  let locationResults = data?.locationsForAutocomplete || [];
  if (limitLocations) locationResults = locationResults.slice(0, limitLocations);

  return locationResults.map((place) => ({
    source: "hudson",
    title: place.displayName,
    description: place.displayAddress,
    farecode: place.code,
    key: place.code,
    servicearea: serviceArea || Session.getConfig().serviceArea,
    type: place.type,
    typestring: place.typeString,
  }));
}

export async function findLocations({
  searchTerm,
  searchCenter,
  searchRadius,
  serviceArea,
  options,
}: be.FindLocationsFunctionArgs): Promise<be.LocationPrediction[]> {
  if (!searchTerm) return [];

  const { data } = await getConfig(["googleMapsDisabled"]);
  const googleDisabled = data?.config?.googleMapsDisabled;

  // Service area could be an array.
  const singleServiceArea = Array.isArray(serviceArea) ? serviceArea[0] : serviceArea;

  if (!options?.forceGoogle) {
    const hudsonResults = await hudsonSearch(
      searchTerm,
      singleServiceArea,
      options?.limitLocations,
    );
    if (hudsonResults.length) {
      if (!googleDisabled) await appendMoreOptionsLink(hudsonResults);
      return hudsonResults;
    }
  }
  return googleDisabled
    ? []
    : await googleSearch(searchTerm, searchCenter, searchRadius, options?.limitLocations);
}
